// PRC-019 · B2: guía «Primeros pasos» con cinco pasos que se marcan solos.
// PRC-042 (boceto G7): barra de progreso, estado de cada paso (Hecho, Siguiente,
// Al terminar el motor), la carga pide solo ventas y evaluar un precio espera
// al motor sin bloquear los demás pasos; a su lado, «Tu motor de precios».
import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { JourneyButton, JourneyCard, JourneyChip, JourneyText, type ChipTone } from '../components/JourneyBasics';
import { Meter } from '../components/JourneyBlocks';
import OverviewEmptyState from '../components/OverviewEmptyState';
import PageHeader, { pageFrameSx } from '../components/PageHeader';
import { SkeletonBlock } from '../components/Skeletons';
import EnginePanel, { engineStateOf, useEngineStatus } from '../components/EnginePanel';
import { J } from '../journeyTokens';
import { formatLongDate } from '../journeyBilling';
import { downloadTemplate, requiredColumns, usePricingOnboarding } from '../onboarding';
import { withReturnTo } from '../navigation';

// PRC-027: cada paso lleva a su pantalla con el origen, que muestra «Volver a Primeros pasos».
const GUIDE = '/primeros-pasos';

type GuideState = 'done' | 'next' | 'pending' | 'engine' | 'engine_failed';

const STATE_CHIP: Record<GuideState, { label: string; tone: ChipTone } | null> = {
  done: { label: 'Hecho', tone: 'ok' },
  next: { label: 'Siguiente', tone: 'sky' },
  pending: null,
  engine: { label: 'Al terminar el motor', tone: 'amber' },
  engine_failed: { label: 'Motor con error', tone: 'danger' },
};

const hhmm = (d: Date) => d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });

interface GuideStepProps {
  index: number;
  state: GuideState;
  title: string;
  description: React.ReactNode;
  note?: React.ReactNode;
  action?: React.ReactNode;
}

const GuideStep: React.FC<GuideStepProps> = ({ index, state, title, description, note, action }) => {
  const chip = STATE_CHIP[state];
  const done = state === 'done';
  const next = state === 'next';
  return (
    <Box component="li" aria-current={next ? 'step' : undefined} sx={{
      display: 'flex', alignItems: 'center', gap: '16px', p: '16px 18px', borderRadius: '12px', flexWrap: { xs: 'wrap', sm: 'nowrap' },
      border: `1px solid ${next ? 'rgba(56,189,248,.45)' : J.border}`, bgcolor: next ? 'rgba(56,189,248,.06)' : 'rgba(15,23,42,.6)',
    }}>
      <Box component="span" aria-hidden sx={{
        flex: '0 0 32px', height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, border: '1px solid',
        ...(done ? { bgcolor: J.ok, borderColor: J.ok, color: '#052e16' } : next ? { bgcolor: J.sky, borderColor: J.sky, color: J.onSky } : { borderColor: J.borderStrong, color: J.body }),
      }}>
        {done ? '✓' : index}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: done ? J.muted : J.text }}>{title}</Box>
          {chip && <JourneyChip tone={chip.tone}>{chip.label}</JourneyChip>}
        </Box>
        <Box sx={{ fontSize: 13, color: J.muted, mt: '2px', lineHeight: 1.5 }}>{description}</Box>
        {note && <Box sx={{ fontSize: 12.5, color: J.body, mt: '4px', fontWeight: 600 }}>{note}</Box>}
      </Box>
      {action}
    </Box>
  );
};

const FirstStepsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = usePricingOnboarding();
  const steps = data?.steps;
  const { data: engine } = useEngineStatus(!!steps?.sales);
  const engineState = engineStateOf(engine);
  const engineReady = engineState === 'ready' || !!engine?.has_ready_model;

  // Ventana estimada del motor (de 30 min a 3 h desde que empezó a entrenar).
  const engineWindow = (() => {
    if (engineState !== 'preparing' || !engine?.started_at) return null;
    const start = new Date(engine.started_at);
    if (Number.isNaN(start.getTime())) return null;
    return `Disponible entre las ${hhmm(new Date(start.getTime() + 30 * 60_000))} y las ${hhmm(new Date(start.getTime() + 3 * 3600_000))}`;
  })();

  const defs: Array<{ key: keyof NonNullable<typeof steps>; title: string; description: string; target: string; actionLabel: string; needsEngine?: boolean }> = [
    { key: 'account', title: 'Crear la cuenta', description: data?.account_created_at ? `Hecho el ${formatLongDate(data.account_created_at)}.` : 'Hecho.', target: '/profile', actionLabel: 'Ver mi perfil' },
    { key: 'sales', title: 'Carga tus ventas', description: 'Solo el histórico de ventas. Clientes y productos son opcionales.', target: '/data-upload', actionLabel: 'Cargar ventas' },
    { key: 'quality', title: 'Revisa la calidad de los datos', description: 'Te señalamos filas incompletas, costes vacíos o precios extraños.', target: '/data-quality', actionLabel: 'Revisar calidad' },
    { key: 'overview', title: 'Mira dónde ganas y pierdes margen', description: 'Overview de los últimos 12 meses.', target: '/margin-center/overview', actionLabel: 'Abrir Overview' },
    { key: 'optimization', title: 'Evalúa un precio', description: 'Una combinación cliente-producto en Optimización.', target: '/optimization-center', actionLabel: 'Evaluar un precio', needsEngine: true },
  ];

  // «Siguiente» es el primer paso pendiente que se puede hacer ya; el de evaluar
  // un precio no bloquea a los demás mientras el motor se prepara.
  const blocked = (d: (typeof defs)[number]) => !!d.needsEngine && !engineReady;
  const nextIndex = defs.findIndex((d) => !steps?.[d.key] && !blocked(d));

  const items = defs.map((d, i) => {
    const done = !!steps?.[d.key];
    const waiting = !done && blocked(d);
    const state: GuideState = done ? 'done' : waiting ? (engineState === 'failed' ? 'engine_failed' : 'engine') : i === nextIndex ? 'next' : 'pending';
    const description = waiting
      ? (engineState === 'failed'
        ? 'El motor de precios no ha terminado bien. Reinténtalo desde «Tu motor de precios».'
        : 'Se activa cuando el motor de precios termine de prepararse.')
      : d.description;
    const note = waiting && engineState === 'preparing' ? (engineWindow ?? 'De 30 min a 3 h según el volumen') : undefined;
    return (
      <GuideStep
        key={d.key}
        index={i + 1}
        state={state}
        title={d.title}
        description={description}
        note={note}
        action={waiting ? undefined : (
          <JourneyButton small kind={state === 'next' ? 'primary' : 'ghost'} onClick={() => navigate(withReturnTo(d.target, GUIDE))}>
            {done ? 'Ver de nuevo' : d.actionLabel}
          </JourneyButton>
        )}
      />
    );
  });

  const done = data?.done ?? 1;
  const total = data?.total ?? 5;

  return (
    <Box sx={{ ...pageFrameSx, display: 'flex', flexDirection: 'column', gap: 3, fontFamily: J.font }}>
      <PageHeader
        eyebrow="Empezar"
        title="Primeros pasos"
        description={done >= total ? 'Completada' : `${done} de ${total} hechos`}
        actions={<Box sx={{ width: { xs: 160, sm: 220 } }}><Meter value={(done / total) * 100} /></Box>}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '3 1 520px', minWidth: 0 }}>
          {isLoading ? <SkeletonBlock h={360} /> : <Box component="ol" sx={{ display: 'flex', flexDirection: 'column', gap: '10px', m: 0, p: 0, listStyle: 'none' }}>{items}</Box>}
        </Box>
        <Box sx={{ flex: '2 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {steps?.sales && engineState !== 'no_data' ? (
            <EnginePanel />
          ) : (
            <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <JourneyText variant="h3">Qué debe llevar el archivo de ventas</JourneyText>
              <JourneyText variant="small">Solo hace falta el histórico de ventas. Columnas obligatorias:</JourneyText>
              <Box component="ul" sx={{ m: 0, pl: '18px', fontSize: 14, lineHeight: 1.6, color: J.body }}>
                {requiredColumns('transactions').map((c) => <li key={c.key}>{c.label} <Box component="span" sx={{ color: J.muted }}>({c.key})</Box></li>)}
              </Box>
              <Box component="button" type="button" onClick={() => downloadTemplate('transactions')} sx={{ all: 'unset', cursor: 'pointer', alignSelf: 'flex-start', color: J.skyText, fontWeight: 600, fontSize: 14 }}>
                Descargar plantilla de ventas
              </Box>
              <JourneyText variant="small">Clientes y productos son opcionales: con ellos el motor afina más. Después de cargar, el motor de precios se prepara solo (de 30 min a 3 h) y mientras tanto Margin Center ya funciona.</JourneyText>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: 12, color: J.muted, '&:before, &:after': { content: '""', flex: 1, height: '1px', bgcolor: J.border } }}>o</Box>
              <JourneyButton kind="ghost" small onClick={() => navigate(withReturnTo('/enviar-archivos', GUIDE))}>Enviar archivos al equipo para que los preparemos</JourneyButton>
            </JourneyCard>
          )}
        </Box>
      </Box>

      {!steps?.sales && !isLoading && (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <JourneyText variant="h3">Así se ve Overview antes de cargar datos</JourneyText>
          <OverviewEmptyState />
        </Box>
      )}
    </Box>
  );
};

export default FirstStepsPage;
